import { FC, useState } from 'react';
import { useSelector } from 'react-redux';

import { intersection, isEmpty, pick } from 'lodash/fp';

import {
    AGE_GROUP_ID_KEY,
    AGE_MAX_KEY,
    AGE_MIN_KEY,
    MEASURE_ID_KEY,
} from '@portal/common/models/data-key';
import { getDataTypePrimaryEntityKey } from '@portal/common/models/data-type';
import { isSingleYearAgeEstimatesExportEnabled as checkIfSingleYearAgeEstimatesExportEnabled } from '@portal/common/models/organization';
import { DataType } from '@portal/common/types';

import config from '../../../config';
import {
    getIsSingleYearAgeDataSuiteSupported,
    getSelectedConditions,
    getSelectedConditionsDataTypes,
    getSelectedConditionsRefinedGranularity,
    getSelectedDataProject,
    getSelectedRefinementFiltersWithFallbackToDefaultFilters,
    getSingleYearAgeGranularity,
} from '../../../store/data-explorer/selectors';
import { getOrganization } from '../../../store/organization/selectors';
import { conditionsToDataFilters } from '../../../utility/data-loader';
import ExportSingleAgeEstimatesButton from './ExportSingleAgeEstimatesButton';
import ExportSingleAgeEstimatesModal from './ExportSingleAgeEstimatesModal';

const ExportSingleAgeEstimates: FC = () => {
    const selectedDataProject = useSelector(getSelectedDataProject);
    const selectedRefinementFilters = useSelector(
        getSelectedRefinementFiltersWithFallbackToDefaultFilters
    );
    const selectedConditions = useSelector(getSelectedConditions);
    const refinedGranularity = useSelector(getSelectedConditionsRefinedGranularity);
    const selectedConditionsDataTypes = useSelector(getSelectedConditionsDataTypes);
    const organization = useSelector(getOrganization);
    const singleYearAgeGranularity = useSelector(getSingleYearAgeGranularity);
    const isDataSuiteSupported = useSelector(getIsSingleYearAgeDataSuiteSupported);

    const [isModalVisible, setModalVisibility] = useState(false);

    const isSingleYearAgeEstimatesExportEnabled =
        checkIfSingleYearAgeEstimatesExportEnabled(organization);

    if (!isDataSuiteSupported) {
        return null;
    }

    const isDataProjectSupported = config.singleYearAgeDataProjects.includes(selectedDataProject);
    if (
        !isDataProjectSupported ||
        !isSingleYearAgeEstimatesExportEnabled ||
        !selectedConditionsDataTypes ||
        !selectedRefinementFilters
    ) {
        return null;
    }

    const unsupportedDataTypes = (selectedConditionsDataTypes || []).filter(
        (type) => !config.singleYearAgeDataTypes.includes(type)
    );
    const isRestrictedDataTypeSelected = !isEmpty(unsupportedDataTypes);
    const isSupportedDataTypeSelected = !isEmpty(
        intersection(selectedConditionsDataTypes, config.singleYearAgeDataTypes)
    );

    const agesRange = {
        min: singleYearAgeGranularity?.[AGE_MIN_KEY],
        max: singleYearAgeGranularity?.[AGE_MAX_KEY],
    };

    if (!isSupportedDataTypeSelected) {
        return (
            <ExportSingleAgeEstimatesButton
                selectedConditionsDataTypes={selectedConditionsDataTypes}
                selectedRefinementFilters={selectedRefinementFilters}
                setModalVisibility={setModalVisibility}
                supportedDataTypes={config.singleYearAgeDataTypes as DataType[]}
                disabled={true}
                selectedFilters={{}}
                granularity={singleYearAgeGranularity}
            />
        );
    }

    if (!agesRange.min || !agesRange.max) {
        return null;
    }

    const filters = {
        ...refinedGranularity,
        ...pick(Object.keys(refinedGranularity), selectedRefinementFilters),
        ...conditionsToDataFilters(selectedConditions),
    };

    delete filters[AGE_GROUP_ID_KEY];
    if (isRestrictedDataTypeSelected) {
        unsupportedDataTypes.forEach((type) => {
            const key = getDataTypePrimaryEntityKey(type);
            delete filters[key];
        });
    }

    const couldReportBeGenerated = !Object.entries(filters).find(([key, value]) => {
        const granularity = singleYearAgeGranularity?.[key] ?? [];
        return isEmpty(intersection(granularity, value as number[]));
    });

    return (
        <>
            <ExportSingleAgeEstimatesButton
                selectedConditionsDataTypes={selectedConditionsDataTypes}
                selectedRefinementFilters={selectedRefinementFilters}
                setModalVisibility={setModalVisibility}
                supportedDataTypes={config.singleYearAgeDataTypes as DataType[]}
                disabled={!couldReportBeGenerated}
                selectedFilters={filters}
                granularity={singleYearAgeGranularity}
            />
            {couldReportBeGenerated && (
                <ExportSingleAgeEstimatesModal
                    isVisible={isModalVisible}
                    supportedAgeRange={agesRange}
                    setVisibility={setModalVisibility}
                    supportedDataTypes={config.singleYearAgeDataTypes as DataType[]}
                    selectedFilters={filters}
                    selectedConditionsDataTypes={selectedConditionsDataTypes}
                    dataProjectId={selectedDataProject}
                    selectedMeasures={
                        (selectedRefinementFilters?.[MEASURE_ID_KEY] as number[]) || []
                    }
                />
            )}
        </>
    );
};

export default ExportSingleAgeEstimates;
